import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import AppForm from "components/Shared/AppForm";

import { useGetDDLAccountListQuery } from "store/services/accountApi";

import { useAuth } from "hooks/useAuth";

import { COUNTRY_LIST, CURRENCY_LIST } from "data/static";

const INITIAL_STATE = {
  name: "",
  displayName: "",
  country: null,
  type: "EntityGeneral",
  isActive: true,
  licenses: 10,
  plan: "Free",
  frequency: "Monthly",
  currency: "",
  account: "",
  category: "Organization",
};

const EntityForm = ({
  entityDetail = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const planTypes = ["Free", "Enterprise"];
  const frequencyTypes = ["Monthly", "Quarterly", "Annually"];

  const navigate = useNavigate();

  const { isSystemAdmin, viewEntity, loggedInUser } = useAuth();

  //   RTK Query
  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm({
    defaultValues: { ...INITIAL_STATE },
  });

  const selectedPlan = watch("plan");
  selectedPlan === "Free" && setValue("licenses", 10);

  // methods
  const handleOnEntitySubmit = (data) => {
    const payload = { ...data, user: loggedInUser?._id };

    delete payload.users;
    delete payload.entityPlans;

    onSubmit(payload);
  };

  React.useEffect(() => {
    if (!entityDetail) return;

    reset({
      name: entityDetail?.name,
      displayName: entityDetail?.displayName,
      currency: entityDetail?.currency,
      account: entityDetail?.account?._id,
      country: entityDetail?.country,
      isActive: entityDetail?.isActive,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityDetail, viewEntity, isSystemAdmin]);

  React.useEffect(() => {
    if (!accounts?.length || !viewEntity) return;
    reset({
      ...getValues(),
      account: !isSystemAdmin ? viewEntity?.entity?.account?._id : "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, viewEntity, isSystemAdmin]);

  return (
    <AppForm loading={isBusy || loadingAccounts}>
      <AppForm.Content>
        <Grid container spacing={5}>
          <Grid item md={6}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Please provide a Name." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Name"
                  placeholder="Enter Entity name"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            {errors.name && (
              <FormHelperText error>{errors.name.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={6}>
            <Controller
              name="displayName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Display Name"
                  placeholder="Enter display name"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="country"
              control={control}
              rules={{ required: "Please select a Country." }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={COUNTRY_LIST ?? []}
                  getOptionLabel={(option) => `${option.cca2} - ${option.name}`}
                  size="small"
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Select country"
                    />
                  )}
                  value={field?.value}
                  onChange={(e, data) => setValue("country", data)}
                />
              )}
            />
            {!!errors.country && (
              <FormHelperText error>{errors.country.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={6}>
            <Controller
              name="currency"
              control={control}
              rules={{ required: "Please select a Country." }}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  id="entity-currency"
                  options={CURRENCY_LIST ?? []}
                  getOptionLabel={(option) =>
                    option && `${option.code}-${option.name}`
                  }
                  size="small"
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Currency"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Select currency"
                    />
                  )}
                  value={field?.value}
                  onChange={(e, data) => setValue("currency", data)}
                />
              )}
            />
            {!!errors.currency && (
              <FormHelperText error>{errors.currency.message}</FormHelperText>
            )}
          </Grid>

          {!entityDetail && (
            <>
              <Grid item md={6}>
                <Controller
                  name="plan"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="select-plan">Plan</InputLabel>

                      <Select
                        labelId="select-plan"
                        id="select-plan"
                        size="small"
                        label="Plan"
                        {...field}
                      >
                        {planTypes.map((plan) => (
                          <MenuItem key={plan} value={plan}>
                            {plan}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item md={6}>
                <Controller
                  name="frequency"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel id="select-frequency">
                        Plan frequency
                      </InputLabel>
                      <Select
                        labelId="select-frequency"
                        id="select-frequency"
                        size="small"
                        label="Plan Frequency"
                        {...field}
                      >
                        {frequencyTypes.map((frequency) => (
                          <MenuItem key={frequency} value={frequency}>
                            {frequency}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item md={6}>
                <Controller
                  name="licenses"
                  control={control}
                  rules={{
                    required: "Please select a Plan.",
                    min: {
                      value: 1,
                      message: "License quantity must be at least 1.",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      placeholder="Enter license quantity"
                      label="License Quantity"
                      size="small"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      disabled={selectedPlan === "Free"}
                      {...field}
                    />
                  )}
                />
                {!!errors.licenses && (
                  <FormHelperText error>
                    {errors.licenses.message}
                  </FormHelperText>
                )}
              </Grid>
            </>
          )}

          <Grid item md={6}>
            <Controller
              name="account"
              control={control}
              rules={{ required: "Please select an Account." }}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="select-account">Account</InputLabel>

                  <Select
                    id="select-account"
                    labelId="select-account"
                    size="small"
                    label="Account"
                    disabled={!isSystemAdmin}
                    {...field}
                  >
                    {accounts?.map((account) => (
                      <MenuItem key={account?._id} value={account?._id}>
                        {account?.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            {!!errors.account && (
              <FormHelperText error>{errors.account.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={6}>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      value={field?.value}
                      disabled={viewEntity?.entity?._id === entityDetail?._id}
                    />
                  }
                  label="Is Active?"
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>

        <Button
          variant="contained"
          onClick={handleSubmit(handleOnEntitySubmit)}
        >
          {entityDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default EntityForm;
